import { useRouter } from 'next/router';
import { useState } from 'react';

import { Typography } from '@/components/typography';
import { useAuthUser } from '@/hooks';
import { useCampaign } from '@/hooks/client';
import { useAppContext } from '@/context';
import { TranslatedLink } from '@/utils/translatedComponents';
import { createNavigationHandler } from '@/utils/navigation';

import useIsIframe from '@/hooks/useIsIframe';
import Logo from '../../../public/beatly-small.svg';
import styles from './Navigation.module.css';
import { NavigationLink } from './NavigationLink';
import { NavigationLinks } from './NavigationLinks';
import { NavigationToggle } from './NavigationToggle';

const OPEN_CONTENT_HUB_IN_NEW_TAB = false;

export function Navigation() {
  const [showMenu, setShowMenu] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebar-expanded');
      return saved === 'true';
    }
    return false;
  });
  const router = useRouter();
  const user = useAuthUser();
  const isIframe = useIsIframe();
  const { campaignId } = useAppContext();

  // Fetch campaign data when we have a campaignId
  const { data: campaignData = {} } = useCampaign(campaignId, !!campaignId && user?.is_client);

  function handleMenu() {
    const newState = !showMenu;
    setShowMenu(newState);
    if (typeof window !== 'undefined') {
      localStorage.setItem('sidebar-expanded', String(newState));
    }
  }

  const filteredNavigation = NavigationLinks({
    user,
    id: campaignId,
    campaign: campaignData
  });

  return (
    <nav
      data-has-subnav={filteredNavigation.some((item) => !!item.subnav?.length)}
      className={`${styles.navigation} ${isIframe ? styles.embedMode : ''}`}
      data-show={showMenu}
    >
      <div className={styles.navigationContent}>
        <div className={styles.navigationContentMain}>
          <div className={styles.navigationContentMainUser}>
            <TranslatedLink
              href={`/connect/${user?.is_client ? 'campaigns' : 'collaborations'}`}
              inherit
              isBlock
              onClick={createNavigationHandler(
                `/connect/${user?.is_client ? 'campaigns' : 'collaborations'}`,
                router.asPath
              )}
            >
              <Logo className={styles.navigationContentMainLogo} />
            </TranslatedLink>

            <div className={styles.navigationContentMainUserContent}>
              <Typography sizes={['body-small']} inherit>
                <strong className={styles.navigationContentMainUserContentName}>
                  {user?.name || '-'}
                </strong>

                {user?.is_client && (
                  <span className={styles.navigationContentMainUserContentTeam}>
                    <Typography sizes={['label-secondary']} inherit>
                      {user?.team?.name || '-'}
                    </Typography>
                  </span>
                )}
              </Typography>
            </div>
          </div>

          <NavigationToggle open={showMenu} onClick={handleMenu} />
        </div>

        <div className={styles.navigationContentLinks}>
          <ul>
            {filteredNavigation.map((link) => (
              <NavigationLink
                user={user}
                openInNewTab={OPEN_CONTENT_HUB_IN_NEW_TAB}
                key={`navigation-link-${link.id}`}
                currentLink={router.asPath}
                onClick={handleMenu}
                link={link}
              />
            ))}
          </ul>
        </div>
      </div>
    </nav>
  );
}
